import { createContext, useContext, useState } from "react";

const LangContext = createContext({ lang: "es", setLang: () => {} });

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("ppr-lang") || "es");
  const switchLang = (l) => {
    setLang(l);
    localStorage.setItem("ppr-lang", l);
    document.documentElement.lang = l;
  };
  return <LangContext.Provider value={{ lang, setLang: switchLang }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
export const tx = (obj, lang) => (typeof obj === "string" ? obj : obj?.[lang] ?? obj?.es ?? "");

export const ui = {
  tabs: {
    clima: { es: "Clima y Alertas", en: "Weather & Alerts" },
    directorio: { es: "Directorio de Emergencia", en: "Emergency Directory" },
    checklists: { es: "Checklists e Imprimibles", en: "Checklists & Printables" },
    evacuacion: { es: "Planes de Evacuación", en: "Evacuation Plans" },
    agua: { es: "Agua y Cisternas", en: "Water & Cisterns" },
    energia: { es: "Energía y Plantas", en: "Power & Generators" },
    cuidados: { es: "Cuidados Especiales", en: "Special Care" },
    huertos: { es: "Huertos y Vehículos", en: "Gardens & Vehicles" },
    sobre: { es: "Sobre Nosotros", en: "About Us" },
    preguntas: { es: "Preguntas", en: "FAQ" },
  },
  hero: {
    kicker: { es: "Plataforma de preparación ante desastres", en: "Disaster preparedness platform" },
    lines: {
      es: ["PUERTO RICO", "PREPARADO", "SIEMPRE."],
      en: ["PUERTO RICO,", "READY", "ALWAYS."],
    },
    sub: {
      es: "Clima en vivo del NWS, directorio de los 78 municipios, checklists imprimibles y guías de supervivencia — todo en un solo lugar, en tu idioma.",
      en: "Live NWS weather, a 78-municipality directory, printable checklists and survival guides — all in one place, in your language.",
    },
    cta1: { es: "Directorio de Emergencia", en: "Emergency Directory" },
    cta2: { es: "Checklists de Preparación", en: "Preparedness Checklists" },
  },
  weather: {
    title: { es: "Panel Meteorológico — Puerto Rico", en: "Weather Dashboard — Puerto Rico" },
    subtitle: { es: "Pronóstico del Servicio Nacional de Meteorología (San Juan)", en: "National Weather Service forecast (San Juan)" },
    refresh: { es: "Actualizar", en: "Refresh" },
    daily: { es: "Pronóstico Diario", en: "Daily Forecast" },
    weekly: { es: "Pronóstico Semanal", en: "Weekly Forecast" },
    alertsTitle: { es: "Alertas Activas en Puerto Rico", en: "Active Alerts in Puerto Rico" },
    noAlerts: { es: "No hay alertas activas para Puerto Rico en este momento.", en: "No active alerts for Puerto Rico right now." },
    live: { es: "En vivo — NWS", en: "Live — NWS" },
    cache: { es: "Caché local (sin conexión)", en: "Local cache (offline)" },
    placeholder: { es: "Datos de ejemplo (sin conexión)", en: "Sample data (offline)" },
    updated: { es: "Actualizado", en: "Updated" },
    wind: { es: "Viento", en: "Wind" },
    nwsNote: {
      es: "Fuente: api.weather.gov (NWS). Algunos textos pueden llegar en inglés según disponibilidad del NWS.",
      en: "Source: api.weather.gov (NWS). Some texts may arrive in Spanish depending on NWS availability.",
    },
    shareForecast: { es: "Compartir pronóstico", en: "Share forecast" },
    status: {
      hurricanes: { es: "Huracanes", en: "Hurricanes" },
      earthquakes: { es: "Terremotos", en: "Earthquakes" },
      floods: { es: "Inundaciones", en: "Floods" },
      climate: { es: "Cambio Climático", en: "Climate Change" },
      seasonOn: { es: "Temporada activa (1 jun – 30 nov)", en: "Season active (Jun 1 – Nov 30)" },
      seasonOff: { es: "Fuera de temporada — prepárese ahora", en: "Off-season — prepare now" },
      quakeText: { es: "PR es sísmicamente activa. Red Sísmica: 787-833-8433", en: "PR is seismically active. Seismic Network: 787-833-8433" },
      climateText: { es: "Aumento de nivel del mar y calor extremo afectan costas y salud.", en: "Sea-level rise and extreme heat affect coasts and health." },
      activeAlerts: { es: "alertas activas", en: "active alerts" },
      noThreat: { es: "Sin amenaza activa", en: "No active threat" },
    },
  },
  directory: {
    title: { es: "Directorio de Emergencia", en: "Emergency Directory" },
    subtitle: { es: "Contactos directos de los 78 municipios, agencias estatales y hospitales", en: "Direct contacts for all 78 municipalities, state agencies and hospitals" },
    search: { es: "Buscar municipio, agencia u hospital...", en: "Search municipality, agency or hospital..." },
    municipalities: { es: "Municipios (78)", en: "Municipalities (78)" },
    agencies: { es: "Agencias Estatales", en: "State Agencies" },
    hospitals: { es: "Hospitales y CDT", en: "Hospitals & CDT" },
    allRegions: { es: "Todas las regiones", en: "All regions" },
    call: { es: "Llamar", en: "Call" },
    omme: { es: "OMME — Manejo de Emergencias Municipal", en: "OMME — Municipal Emergency Management" },
    map: { es: "Mapa de Refugios", en: "Shelter Map" },
    refugios: { es: "Refugios (Direcciones)", en: "Shelters (Addresses)" },
    policeFireNote: {
      es: "Bomberos en PR son estatales (91 estaciones en 6 zonas). La Policía Municipal se publica por cada alcaldía; si no contesta, llame a la Policía de PR o al 911.",
      en: "Fire service in PR is state-run (91 stations in 6 zones). Municipal Police numbers are published by each mayor's office; if unanswered, call PR Police or 911.",
    },
    verifyNote: {
      es: "Teléfonos OMME de la lista oficial de NMEAD (la más reciente consolidada; San Juan actualizado a su línea de despacho vigente). Pueden cambiar con cada administración municipal: si un número no responde, llame al 911 o a NMEAD (787-724-0124) y verifique con su alcaldía los contactos de Policía Municipal, Bomberos y COE.",
      en: "OMME phones from NMEAD's official list (latest consolidated version; San Juan updated to its current dispatch line). They may change with each municipal administration: if a number does not answer, call 911 or NMEAD (787-724-0124) and verify Municipal Police, Fire and COE contacts with your mayor's office.",
    },
    hospitalsNote: {
      es: "En una emergencia médica llame al 911. Cada municipio cuenta con un CDT (Centro de Diagnóstico y Tratamiento) para atención de urgencias.",
      en: "In a medical emergency call 911. Every municipality has a CDT (Diagnosis & Treatment Center) for urgent care.",
    },
    results: { es: "resultados", en: "results" },
    subscribeTitle: { es: "Resumen Semanal Familiar", en: "Weekly Family Summary" },
    noResults: { es: "Sin resultados para esa búsqueda.", en: "No results for that search." },
  },
  checklists: {
    title: { es: "Checklists e Imprimibles", en: "Checklists & Printables" },
    subtitle: { es: "Listas interactivas con guardado automático en este dispositivo", en: "Interactive lists with automatic saving on this device" },
    completed: { es: "Completado", en: "Completed" },
    reset: { es: "Reiniciar lista", en: "Reset list" },
    print: { es: "Imprimir / Guardar PDF", en: "Print / Save PDF" },
    saved: { es: "Progreso guardado localmente", en: "Progress saved locally" },
    printTitle: { es: "Lista de preparación — preparatepr.org", en: "Preparedness list — preparatepr.org" },
  },
  fab: {
    label: { es: "Contactos de Emergencia", en: "Emergency Contacts" },
  },
  lowBandwidth: { es: "Modo ahorro de datos", en: "Data-saver mode" },
  guide: {
    chapter: { es: "Capítulo", en: "Chapter" },
    steps: { es: "Pasos clave", en: "Key steps" },
  },
  about: {
    title: { es: "Sobre Nosotros", en: "About Us" },
    missionTitle: { es: "Nuestra Misión", en: "Our Mission" },
    mission: {
      es: "En preparatepr.org, nos dedicamos a capacitar, educar y equipar a la ciudadanía de Puerto Rico para enfrentar situaciones de emergencia, desastres naturales y retos comunitarios con resiliencia y determinación.",
      en: "At preparatepr.org, we are dedicated to training, educating and equipping the people of Puerto Rico to face emergencies, natural disasters and community challenges with resilience and determination.",
    },
    missionP2: {
      es: "Nuestro objetivo principal es ser el recurso digital de referencia para la preparación familiar y comunitaria en la isla, proporcionando guías prácticas, planes de contingencia, listas de verificación y herramientas accesibles que permitan a cada hogar tomar decisiones informadas antes, durante y después de un evento crítico.",
      en: "Our main goal is to be the reference digital resource for family and community preparedness on the island, providing practical guides, contingency plans, checklists and accessible tools that let every household make informed decisions before, during and after a critical event.",
    },
    goalsTitle: { es: "Lo Que Queremos Lograr", en: "What We Aim to Achieve" },
    goals: [
      {
        t: { es: "Cultura de Prevención", en: "Culture of Prevention" },
        d: { es: "Transformar la respuesta ante emergencias de ser reactiva a ser un hábito constante de preparación y planificación en cada comunidad boricua.", en: "Transform emergency response from reactive to a constant habit of preparation and planning in every Boricua community." },
      },
      {
        t: { es: "Educación Accesible", en: "Accessible Education" },
        d: { es: "Ofrecer tutoriales, artículos y recursos claros, libres de tecnicismos, diseñados para el contexto geográfico y social de Puerto Rico.", en: "Offer tutorials, articles and clear resources, free of jargon, designed for Puerto Rico's geographic and social context." },
      },
      {
        t: { es: "Autoeficiencia Comunitaria", en: "Community Self-Sufficiency" },
        d: { es: "Fomentar la colaboración entre vecinos para que cada sector pueda responder de manera rápida y organizada mientras llega la ayuda oficial.", en: "Foster collaboration between neighbors so each sector can respond quickly and in an organized way while official help arrives." },
      },
      {
        t: { es: "Resiliencia Activa", en: "Active Resilience" },
        d: { es: "Promover una mentalidad donde la consigna \"Preparado Siempre\" se traduzca en familias seguras, suministros adecuados y comunidades protegidas.", en: "Promote a mindset where the motto \"Preparado Siempre\" translates into safe families, adequate supplies and protected communities." },
      },
    ],
    contactTitle: { es: "Contacto", en: "Contact" },
    email: "preparatepr78@gmail.com",
    resourcesTitle: { es: "Recursos Oficiales del Gobierno de PR", en: "Official PR Government Resources" },
    disclaimerTitle: { es: "Aviso Importante", en: "Important Notice" },
    disclaimer: {
      es: "Este sitio es informativo y no sustituye las instrucciones oficiales. Los números municipales deben verificarse con cada alcaldía. En una emergencia inmediata, llame siempre al 911.",
      en: "This site is informational and does not replace official instructions. Municipal numbers must be verified with each mayor's office. In an immediate emergency, always call 911.",
    },
  },
  footer: {
    tagline: { es: "Hecho en Puerto Rico, para Puerto Rico.", en: "Made in Puerto Rico, for Puerto Rico." },
  },
};
